/**
 * WRMS Calendar Service — Digi Sahyadri
 * Manages company calendar (weekends auto-detected + public holidays from Firestore)
 */

import { db } from '../../config/firebase';
import {
  collection,
  addDoc,
  getDocs,
  deleteDoc,
  doc,
  query,
  where,
  serverTimestamp,
} from 'firebase/firestore';

const COLLECTION = 'wrms_company_calendar';

/**
 * Check if a given date is a non-working day (Saturday, Sunday, or public holiday).
 * @param {Date|string} date
 * @returns {Promise<{ isNonWorking: boolean, type: string, description: string }>}
 */
export const isNonWorkingDay = async (date) => {
  const d = new Date(date);
  const dayOfWeek = d.getDay();

  // Saturday (6) or Sunday (0)
  if (dayOfWeek === 0 || dayOfWeek === 6) {
    return {
      isNonWorking: true,
      type: dayOfWeek === 0 ? 'sunday' : 'saturday',
      description: dayOfWeek === 0 ? 'Sunday' : 'Saturday',
    };
  }

  // Check public holidays
  const dateStr = formatDateStr(d);
  const q = query(
    collection(db, COLLECTION),
    where('dateStr', '==', dateStr),
    where('type', '==', 'public_holiday')
  );
  const snap = await getDocs(q);

  if (!snap.empty) {
    const holiday = snap.docs[0].data();
    return {
      isNonWorking: true,
      type: 'public_holiday',
      description: holiday.description || 'Public Holiday',
    };
  }

  return { isNonWorking: false, type: 'working_day', description: 'Working Day' };
};

/**
 * Client-side only check (no async) — just checks weekend for immediate UI gating
 */
export const isWeekend = (date) => {
  const d = new Date(date);
  const day = d.getDay();
  return day === 0 || day === 6;
};

/**
 * Get all public holidays for a given year
 */
export const getPublicHolidays = async (year = null) => {
  const yr = year || new Date().getFullYear();
  const q = query(
    collection(db, COLLECTION),
    where('year', '==', yr),
    where('type', '==', 'public_holiday')
  );
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
};

/**
 * Add a public holiday (HR/Admin)
 */
export const addPublicHoliday = async (date, description, addedBy = null) => {
  const d = new Date(date);
  return addDoc(collection(db, COLLECTION), {
    date: date,
    dateStr: formatDateStr(d),
    year: d.getFullYear(),
    month: d.getMonth() + 1,
    type: 'public_holiday',
    description: description || 'Public Holiday',
    addedBy,
    createdAt: serverTimestamp(),
  });
};

/**
 * Delete a public holiday
 */
export const deletePublicHoliday = async (holidayId) => {
  await deleteDoc(doc(db, COLLECTION, holidayId));
};

/**
 * Get all non-working dates for a month (for calendar UI highlights)
 */
export const getNonWorkingDatesForMonth = async (year, month) => {
  const result = [];

  // Add weekends
  const daysInMonth = new Date(year, month, 0).getDate();
  for (let d = 1; d <= daysInMonth; d++) {
    const date = new Date(year, month - 1, d);
    if (date.getDay() === 0 || date.getDay() === 6) {
      result.push({ date: formatDateStr(date), type: date.getDay() === 0 ? 'sunday' : 'saturday' });
    }
  }

  // Add public holidays
  const holidays = await getPublicHolidays(year);
  holidays.forEach(h => {
    if (h.month === month) {
      result.push({ date: h.dateStr, type: 'public_holiday', description: h.description });
    }
  });

  return result;
};

const formatDateStr = (date) => {
  const d = new Date(date);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};
